import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { Users, Brain, Code, Network, Award, Rocket, BookOpen, Clock, MapPin, Star, AlertCircle, Loader2, ChevronLeft, ChevronRight, Instagram, Linkedin, MessageCircle } from "lucide-react";
import Navbar from '../components/Navbar';
import { Footer2 } from '../components/Footer2';
import { supabase } from '../lib/supabase';

const Events = () => {
  // State management
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUpcomingPage, setCurrentUpcomingPage] = useState(1);
  const [currentRecentPage, setCurrentRecentPage] = useState(1);
  
  const eventsPerPage = 6;
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true, threshold: 0.1 });

  // Icon mapping for event types
  const getEventIcon = (eventType) => {
    const iconMap = {
      'workshop': <Brain className="h-6 w-6" />,
      'seminar': <BookOpen className="h-6 w-6" />,
      'hackathon': <Rocket className="h-6 w-6" />,
      'social': <Users className="h-6 w-6" />,
      'meeting': <Star className="h-6 w-6" />,
      'conference': <Award className="h-6 w-6" />,
      'networking': <Network className="h-6 w-6" />,
      'competition': <Award className="h-6 w-6" />
    };
    return iconMap[eventType] || <Star className="h-6 w-6" />;
  };

  // Format date and time
  const formatEventDateTime = (startDateTime, endDateTime) => {
    const start = new Date(startDateTime);
    const end = new Date(endDateTime);
    
    const dateOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    const timeOptions = { hour: 'numeric', minute: '2-digit', hour12: true };
    
    const formattedDate = start.toLocaleDateString('en-US', dateOptions);
    const startTime = start.toLocaleTimeString('en-US', timeOptions);
    const endTime = end.toLocaleTimeString('en-US', timeOptions);
    
    return {
      date: formattedDate,
      time: `${startTime} - ${endTime}`
    };
  };

  // Fetch events from database
  const fetchEvents = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch all published events
      const { data: events, error: fetchError } = await supabase
        .from('events')
        .select(`
          id,
          title,
          description,
          event_type,
          location,
          location_type,
          virtual_link,
          start_datetime,
          end_datetime,
          slug,
          status,
          is_featured,
          image_url
        `)
        .eq('status', 'published')
        .order('start_datetime', { ascending: true });

      if (fetchError) {
        throw fetchError;
      }

      // Separate upcoming and recent events
      const now = new Date();
      const upcoming = [];
      const recent = [];

      events?.forEach(event => {
        const eventDate = new Date(event.start_datetime);
        const formattedDateTime = formatEventDateTime(event.start_datetime, event.end_datetime);
        
        const formattedEvent = {
          id: event.id,
          icon: getEventIcon(event.event_type),
          title: event.title,
          category: event.event_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Event',
          description: event.description || 'No description available.',
          date: formattedDateTime.date,
          time: formattedDateTime.time,
          location: event.location_type === 'virtual' ? 'Virtual Event' : (event.location || 'TBA'),
          link: `/events/${event.slug}`,
          is_featured: event.is_featured,
          virtual_link: event.virtual_link,
          image_url: event.image_url
        };

        if (eventDate >= now) {
          upcoming.push(formattedEvent);
        } else {
          recent.push(formattedEvent);
        }
      });

      // Sort recent events by date (most recent first)
      recent.sort((a, b) => new Date(b.date) - new Date(a.date));

      setUpcomingEvents(upcoming);
      setRecentEvents(recent);

    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch events on component mount
  useEffect(() => {
    fetchEvents();
  }, []);

  

  // Pagination logic for upcoming events
  const totalUpcomingPages = Math.ceil(upcomingEvents.length / eventsPerPage);
  const upcomingStartIndex = (currentUpcomingPage - 1) * eventsPerPage;
  const upcomingEndIndex = upcomingStartIndex + eventsPerPage;
  const currentUpcomingEvents = upcomingEvents.slice(upcomingStartIndex, upcomingEndIndex);

  // Pagination logic for recent events
  const totalRecentPages = Math.ceil(recentEvents.length / eventsPerPage);
  const recentStartIndex = (currentRecentPage - 1) * eventsPerPage;
  const recentEndIndex = recentStartIndex + eventsPerPage;
  const currentRecentEvents = recentEvents.slice(recentStartIndex, recentEndIndex);

  const handleUpcomingPageChange = (page) => {
    setCurrentUpcomingPage(page);
    document.getElementById('upcoming-events-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleRecentPageChange = (page) => {
    setCurrentRecentPage(page);
    document.getElementById('recent-events-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleUpcomingPrevPage = () => {
    if (currentUpcomingPage > 1) {
      handleUpcomingPageChange(currentUpcomingPage - 1);
    }
  };

  const handleUpcomingNextPage = () => {
    if (currentUpcomingPage < totalUpcomingPages) {
      handleUpcomingPageChange(currentUpcomingPage + 1);
    }
  };

  const handleRecentPrevPage = () => {
    if (currentRecentPage > 1) {
      handleRecentPageChange(currentRecentPage - 1);
    }
  };

  const handleRecentNextPage = () => {
    if (currentRecentPage < totalRecentPages) {
      handleRecentPageChange(currentRecentPage + 1);
    }
  };

  // Custom Event Card Component
  const EventCard = ({ event, index, delay = 0 }) => (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: delay + index * 0.1 }}
      viewport={{ once: true }}
      whileHover={{ y: -5 }}
      className="bg-white border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:shadow-[12px_12px_0px_0px_rgba(3,149,252,0.3)] transition-all duration-300 group"
    >
      {/* Event Image */}
      {event.image_url ? (
        <div className="h-32 sm:h-48 bg-gray-200 overflow-hidden">
          <img 
            src={event.image_url} 
            alt={event.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.classList.add('hidden');
            }}
          />
        </div>
      ) : (
        <div className="h-32 sm:h-48 bg-gradient-to-br from-[#0395fc] to-blue-600 flex items-center justify-center">
          <div className="text-center text-white">
            <Star className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-2 opacity-50" />
            <span className="text-xs sm:text-sm font-medium opacity-75">Event Image</span>
          </div>
        </div>
      )}
      
      <div className="p-3 sm:p-6">
        {/* Category Badge */}
        <div className="flex items-center justify-between mb-2 sm:mb-4">
          <span className="inline-block bg-[#0395fc] text-white px-2 py-1 sm:px-3 sm:py-1 text-xs font-bold uppercase tracking-wider border-2 border-black">
            {event.category}
          </span>
          <div className="text-[#0395fc] group-hover:scale-110 transition-transform duration-300">
            {event.icon}
          </div>
        </div>

        {/* Event Title */}
        <h3 className="text-lg sm:text-xl font-black mb-2 sm:mb-3 group-hover:text-[#0395fc] transition-colors duration-300">
          {event.title}
        </h3>

        {/* Event Details */}
        <div className="space-y-1 sm:space-y-2 mb-3 sm:mb-4 text-xs sm:text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <Clock className="size-3 sm:size-4" />
            <span>{new Date(event.date).toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="size-3 sm:size-4" />
            <span>{event.time}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="size-3 sm:size-4" />
            <span>{event.location}</span>
          </div>
        </div>

        {/* Description */}
        <p className="text-gray-700 mb-3 sm:mb-6 leading-relaxed text-sm sm:text-base">
          {event.description && event.description.length > 150 
            ? `${event.description.substring(0, 150)}...` 
            : event.description}
        </p>

        {/* Learn More Button */}
        <motion.a
          href={event.link}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="inline-block bg-[#0395fc] hover:bg-[#0070c0] text-white px-4 py-2 sm:px-6 sm:py-3 font-bold border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all duration-200 text-sm sm:text-base"
        >
          Learn More
        </motion.a>
      </div>
    </motion.div>
  );

  // Pagination Component
  const PaginationControls = ({ 
    currentPage, 
    totalPages, 
    onPageChange, 
    onPrevPage, 
    onNextPage, 
    startIndex, 
    endIndex, 
    totalItems,
    label 
  }) => (
    <div className="container mx-auto px-4 max-w-6xl mt-12">
      <div className="flex flex-col items-center gap-6">
        {/* Page Info */}
        <div className="text-center">
          <p className="text-lg font-medium text-gray-700">
            Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} {label}
          </p>
        </div>
        
        {/* Pagination Buttons */}
        {totalPages > 1 && (
          <div className="flex items-center gap-4">
            {/* Previous Button */}
            <button
              onClick={onPrevPage}
              disabled={currentPage === 1}
              className={`flex items-center gap-2 px-4 py-2 font-bold border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all duration-200 ${
                currentPage === 1
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-black hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px]'
              }`}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>
            
            {/* Page Numbers */}
            <div className="flex gap-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  className={`w-10 h-10 font-bold border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all duration-200 ${
                    currentPage === page
                      ? 'bg-[#0395fc] text-white'
                      : 'bg-white text-black hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px]'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
            
            {/* Next Button */}
            <button
              onClick={onNextPage}
              disabled={currentPage === totalPages}
              className={`flex items-center gap-2 px-4 py-2 font-bold border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all duration-200 ${
                currentPage === totalPages
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-black hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px]'
              }`}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Enhanced Hero Section */}
      <section ref={heroRef} className="pt-24 pb-16 bg-cover bg-center bg-no-repeat relative overflow-hidden" style={{ backgroundImage: 'url(/5.jpg)' }}>
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black/50 z-0"></div>
        
        {/* Animated background elements */}
        <motion.div 
          className="absolute top-20 right-20 w-32 h-32 bg-white/10 rounded-full blur-2xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-20 left-10 w-24 h-24 bg-yellow-300/20 rounded-full blur-xl"
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.4, 0.7, 0.4]
          }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />

        <div className="container mx-auto px-4 max-w-7xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Text content */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={isHeroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="text-white"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={isHeroInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full border border-white/30 text-sm font-bold mb-6 backdrop-blur-sm"
              >
                <Star className="size-4" />
                Upcoming & Recent Events
              </motion.div>

              <motion.h1 
                initial={{ opacity: 0, y: 30 }}
                animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="text-5xl lg:text-6xl font-black mb-6 leading-tight"
              >
                DMLS Events
              </motion.h1>

              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="text-xl md:text-2xl mb-8 leading-relaxed text-white/90"
              >
                Join our community of AI/ML enthusiasts through workshops, networking events, 
                competitions, and educational sessions designed to advance your machine learning journey.
              </motion.p>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.7 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <motion.a
                  href="#upcoming-events-section"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-[#0395fc] px-8 py-4 font-bold border-4 border-white shadow-[4px_4px_0px_0px_rgba(255,255,255,0.3)] hover:shadow-[6px_6px_0px_0px_rgba(255,255,255,0.3)] transition-all duration-200 text-center"
                >
                  View Upcoming Events
                </motion.a>
                <motion.a
                  href="#recent-events-section"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-transparent text-white px-8 py-4 font-bold border-4 border-white hover:bg-white hover:text-[#0395fc] transition-all duration-200 text-center"
                >
                  Browse Recent Events
                </motion.a>
              </motion.div>
            </motion.div>

            {/* Right side - Event stats */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={isHeroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-white"
            >
              <div className="grid grid-cols-2 gap-6">
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isHeroInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="bg-white/20 backdrop-blur-sm p-6 border border-white/30 text-center"
                >
                  <div className="text-4xl font-black mb-2">{upcomingEvents.length}</div>
                  <div className="text-sm uppercase tracking-wider">Upcoming Events</div>
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isHeroInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  className="bg-white/20 backdrop-blur-sm p-6 border border-white/30 text-center"
                >
                  <div className="text-4xl font-black mb-2">{recentEvents.length}</div>
                  <div className="text-sm uppercase tracking-wider">Recent Events</div>
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isHeroInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  className="bg-white/20 backdrop-blur-sm p-6 border border-white/30 text-center"
                >
                  <div className="text-4xl font-black mb-2">100+</div>
                  <div className="text-sm uppercase tracking-wider">Attendees across all events</div>
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isHeroInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.6, delay: 0.7 }}
                  className="bg-white/20 backdrop-blur-sm p-6 border border-white/30 text-center"
                >
                  <div className="text-4xl font-black mb-2">10</div>
                  <div className="text-sm uppercase tracking-wider">Team Members</div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Upcoming Events Section */}
      <section id="upcoming-events-section" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-black mb-6">Upcoming Events</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't miss out on these exciting opportunities to learn, network, and grow your ML skills. 
              Register early to secure your spot!
            </p>
          </motion.div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-[#0395fc]" />
                <span className="text-lg font-medium text-muted-foreground">Loading events...</span>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mx-auto max-w-md">
              <div className="flex items-center gap-3 mb-3">
                <AlertCircle className="w-6 h-6 text-red-600" />
                <h3 className="font-bold text-red-800">Error Loading Events</h3>
              </div>
              <p className="text-red-700 mb-4">{error}</p>
              <button
                onClick={fetchEvents}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 font-medium"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Events Grid */}
          {!isLoading && !error && (
            <>
              {currentUpcomingEvents.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {currentUpcomingEvents.map((event, index) => (
                    <EventCard key={event.id || index} event={event} index={index} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-600 mb-2">No Upcoming Events</h3>
                  <p className="text-gray-500">
                    Stay tuned for exciting new events! Check back soon for updates.
                  </p>
                </div>
              )}
            </>
          )}

          {/* Upcoming Events Pagination */}
          {!isLoading && !error && totalUpcomingPages > 1 && (
            <PaginationControls
              currentPage={currentUpcomingPage}
              totalPages={totalUpcomingPages}
              onPageChange={handleUpcomingPageChange}
              onPrevPage={handleUpcomingPrevPage}
              onNextPage={handleUpcomingNextPage}
              startIndex={upcomingStartIndex}
              endIndex={upcomingEndIndex}
              totalItems={upcomingEvents.length}
              label="upcoming events"
            />
          )}
        </div>
      </section>

      {/* Recent Events Section */}
      <section id="recent-events-section" className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-black mb-6">Recent Events</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore what we've been up to recently. From workshops to networking events, 
              see the amazing activities that have shaped our community.
            </p>
          </motion.div>

          {/* Recent Events Grid */}
          {!isLoading && !error && (
            <>
              {currentRecentEvents.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {currentRecentEvents.map((event, index) => (
                    <EventCard key={event.id || index} event={event} index={index} delay={0.2} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-600 mb-2">No Recent Events</h3>
                  <p className="text-gray-500">
                    Once we start hosting events, they'll appear here.
                  </p>
                </div>
              )}
            </>
          )}

          {/* Recent Events Pagination */}
          {!isLoading && !error && totalRecentPages > 1 && (
            <PaginationControls
              currentPage={currentRecentPage}
              totalPages={totalRecentPages}
              onPageChange={handleRecentPageChange}
              onPrevPage={handleRecentPrevPage}
              onNextPage={handleRecentNextPage}
              startIndex={recentStartIndex}
              endIndex={recentEndIndex}
              totalItems={recentEvents.length}
              label="recent events"
            />
          )}
        </div>
      </section>

      {/* Enhanced Call to Action */}
      <section className="py-20 bg-gradient-to-r from-gray-900 to-black text-white">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              Never Miss an Event
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Join our community and stay updated with the latest workshops, networking events, 
              and learning opportunities in machine learning and AI.
            </p>
          </motion.div>

          <div className="max-w-md mx-auto mb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm p-8 border border-white/20 rounded-lg"
            >
              <Users className="size-12 text-[#0395fc] mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-3">Join Our Community</h3>
              <p className="text-gray-300 mb-6">
                Get notified about new events, workshops, and exclusive member opportunities.
              </p>
              <motion.a
                href="https://airtable.com/appbzvXorTGCwSsy9/pagrrVOHqpocEY5IX/form"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-block bg-white text-black px-6 py-3 font-bold border-2 border-white hover:bg-gray-100 transition-all duration-200 rounded-lg"
              >
                Subscribe Now
              </motion.a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <p className="text-gray-400 mb-4">Follow us on social media for real-time updates</p>
            <div className="flex justify-center gap-4">
              <motion.a
                href="https://www.instagram.com/dalmlsociety/"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="bg-white/20 p-3 rounded-full hover:bg-white hover:text-black transition-all duration-200"
              >
                <Instagram className="size-5" />
              </motion.a>
              <motion.a
                href="https://www.linkedin.com/company/dalmlsociety/"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="bg-white/20 p-3 rounded-full hover:bg-white hover:text-black transition-all duration-200"
              >
                <Linkedin className="size-5" />
              </motion.a>
              <motion.a
                href="https://discord.com/invite/47Pyqyhnuu?fbclid=PAZXh0bgNhZW0CMTEAAaf3CY6keaV8UQoHI_ZnlsAlax2Ea_2Sz1LFNBZN6arK2OVmRWSanx7MTnYH_A_aem_fCfe5I9D6Z0mA2c8PfV6cg"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="bg-white/20 p-3 rounded-full hover:bg-white hover:text-black transition-all duration-200"
              >
                <MessageCircle className="size-5" />
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer2 />
    </div>
  );
};

export default Events;